import { CheckCircle2, Wine } from "lucide-react";
import { fmtDate } from "@/lib/booking";

export default function BookingConfirmed({ booking, onReset }) {
  const guests = Number(booking.guests) || 0;
  const cheeseboards = Number(booking.cheeseboards) || 0;
  const tasting = guests * 15;
  const cheese = cheeseboards * 42;
  const voucherApplied = Boolean(booking.voucher_code);
  const onArrival = booking.payment_method === "on_arrival";
  const paid = booking.amount_paid != null ? Number(booking.amount_paid) : tasting + cheese;
  const amountDue = booking.amount_due != null ? Number(booking.amount_due) : tasting + cheese - (voucherApplied ? tasting : 0);

  return (
    <div
      data-testid="booking-confirmation"
      className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-8 md:p-12 text-center animate-fade-up"
    >
      <div className="mx-auto h-16 w-16 rounded-full bg-[#4A5D23]/10 flex items-center justify-center mb-6">
        <CheckCircle2 className="h-9 w-9 text-[#4A5D23]" />
      </div>
      <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#1C1917]">
        You're booked in, {booking.name.split(" ")[0]}!
      </h2>
      <p className="mt-4 text-[#57534E] text-lg">
        {onArrival
          ? "Booking confirmed. "
          : paid === 0
          ? "Booking confirmed with your gift voucher. "
          : "Payment received. "}
        A confirmation email with your booking details and our full bar menu has been sent to{" "}
        <span className="font-semibold text-[#1C1917]">{booking.email}</span>.
      </p>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-left">
        <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
          <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold">Date</p>
          <p className="mt-1 font-medium text-[#1C1917]">{fmtDate(booking.date)}</p>
        </div>
        <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
          <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold">Time</p>
          <p className="mt-1 font-medium text-[#1C1917]">{booking.time_slot}</p>
        </div>
        <div className="rounded-xl bg-[#FDFBF7] border border-[#E7E5E4] p-4">
          <p className="text-xs uppercase tracking-wider text-[#57534E] font-semibold">Guests</p>
          <p className="mt-1 font-medium text-[#1C1917]">{booking.guests}</p>
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-[#4A5D23]/20 bg-[#4A5D23]/5 p-4 text-left text-sm" data-testid="confirmation-pricing">
        <div className="flex justify-between text-[#57534E]">
          <span>Wine tasting · $15.00 × {guests}</span>
          <span className="font-medium text-[#1C1917]">${tasting.toFixed(2)}</span>
        </div>
        {cheeseboards > 0 && (
          <div className="flex justify-between text-[#57534E] mt-1.5">
            <span>Cheeseboards · $42.00 × {cheeseboards}</span>
            <span className="font-medium text-[#1C1917]">${cheese.toFixed(2)}</span>
          </div>
        )}
        {voucherApplied && (
          <div className="flex justify-between text-[#722F37] mt-1.5" data-testid="confirmation-voucher-row">
            <span>Gift voucher {booking.voucher_code} · wine tasting covered</span>
            <span className="font-medium">−${tasting.toFixed(2)}</span>
          </div>
        )}
        <div className="flex justify-between mt-2 pt-2 border-t border-[#4A5D23]/20 font-semibold text-[#1C1917]">
          <span>{onArrival ? "Due on arrival" : "Paid"}</span>
          <span data-testid="confirmation-total">${(onArrival ? amountDue : paid).toFixed(2)} AUD</span>
        </div>
        {onArrival && (
          <p className="mt-2 text-xs text-[#57534E]" data-testid="confirmation-on-arrival-note">
            Payment will be collected upon arrival at the cellar door.
          </p>
        )}
      </div>

      {booking.wine_club && (
        <p className="mt-6 inline-flex items-center gap-2 text-sm text-[#722F37] font-medium bg-[#722F37]/8 px-4 py-2 rounded-full">
          <Wine className="h-4 w-4" /> You've joined the Gledswood Wine Club — welcome!
        </p>
      )}

      <div className="mt-8 rounded-2xl overflow-hidden border border-[#E7E5E4]">
        <img
          src="https://customer-assets.emergentagent.com/job_wine-tasting-book/artifacts/omxl8ys6_IMG_3860.webp"
          alt="Bar menu grazing board"
          className="w-full h-48 object-cover"
        />
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <a
          href={`/manage/${booking.manage_token}`}
          data-testid="manage-booking-link"
          className="px-8 py-3 rounded-full bg-[#4A5D23] text-white font-semibold hover:bg-[#3D4D1D] transition-all"
        >
          Cancel or reschedule
        </a>
        <a
          href="/"
          data-testid="new-booking-btn"
          onClick={onReset}
          className="px-8 py-3 rounded-full border border-[#4A5D23] text-[#4A5D23] font-semibold hover:bg-[#4A5D23] hover:text-white transition-all"
        >
          Back to site
        </a>
      </div>
      <p className="mt-4 text-xs text-[#57534E]">
        This link is also included in your confirmation email.
      </p>
    </div>
  );
}
